/**
 * Storefront Core — pure URL construction for the public site.
 *
 * Presentation-neutral on purpose: no React, no data fetching, no hostname logic, nothing
 * theme-specific. Any header/footer/homepage/card implementation (current or a future theme)
 * builds its links through here instead of concatenating `${langPrefix}/...` by hand.
 *
 * LOCALE-PREFIX CONVENTION (verified against routing.ts / middleware.ts, Phase 1A):
 * The homepage, Header, Footer and every menu link omit the prefix for the default language
 * (`/pages/x` for "en", `/tr/pages/x` for "tr"). next-intl runs in its default
 * `localePrefix: 'always'` mode, so the unprefixed English URL is redirected to `/en/...` by the
 * middleware — valid, just one extra hop. The category page, product detail page and their
 * breadcrumbs instead always write the explicit `/${lang}` form. This module follows the
 * former (it is what the components migrated in Phase 1A already emitted) and does NOT
 * attempt a site-wide URL migration; see `localizedPath`'s `alwaysPrefix` option for callers
 * that need the explicit form.
 */

import { routing } from "@/i18n/routing";

/** Content token an admin places in a CMS page to turn it into the storefront's product
 * listing (filters + grid) — see components/pages/ShopFiltersTemplate. Defined here (not in
 * the server-only component) so client code and helpers can reference the same contract. */
export const SHOP_FILTERS_TEMPLATE_TOKEN = "{{SHOP_FILTERS_TEMPLATE}}";

const DEFAULT_LANGUAGE = routing.defaultLocale;

/** Trimmed, lower-cased language code; falls back to the default language when empty. */
export function normalizeLanguage(language?: string | null): string {
  const trimmed = language?.trim().toLowerCase();
  return trimmed ? trimmed : DEFAULT_LANGUAGE;
}

/** "" for the default language, "/tr" (etc.) for every other one. */
export function languagePrefix(language?: string | null): string {
  const lang = normalizeLanguage(language);
  return lang === DEFAULT_LANGUAGE ? "" : `/${lang}`;
}

/** Prefixes an app-relative path (must start with "/") with the language segment. The root
 * path "/" resolves to the language home ("/" for default, "/tr" otherwise). */
export function localizedPath(
  language: string | null | undefined,
  path: string,
  options: { alwaysPrefix?: boolean } = {},
): string {
  const prefix = options.alwaysPrefix ? `/${normalizeLanguage(language)}` : languagePrefix(language);
  if (path === "/" || path === "") return prefix || "/";
  return `${prefix}${path.startsWith("/") ? path : `/${path}`}`;
}

export function homeHref(language?: string | null): string {
  return localizedPath(language, "/");
}

export function pageHref(language: string | null | undefined, slug: string): string {
  return localizedPath(language, `/pages/${slug}`);
}

export function categoryHref(language: string | null | undefined, slug: string): string {
  return localizedPath(language, `/categories/${slug}`);
}

export function productHref(language: string | null | undefined, slug: string): string {
  return localizedPath(language, `/products/${slug}`);
}

/** Href of the category index (app/[lang]/categories/page.tsx — the existing category-list presentation over the domain's
 * public categories). Every "View all categories" control is built here, in one place. */
export function categoriesIndexHref(language?: string | null): string {
  return localizedPath(language, "/categories");
}

/**
 * The storefront's canonical product-listing URL: the localized CMS page whose content
 * contains SHOP_FILTERS_TEMPLATE_TOKEN. `slug` is that page's slug IN `language`, resolved by
 * the caller from the PageTranslation relationship (GET /api/public/pages/by-token).
 *
 * Returns null when the page can't be resolved — callers must then hide the link rather than
 * emit a guessed URL (there is no `/shop` route).
 */
export function productListingHref(
  language: string | null | undefined,
  slug: string | null | undefined,
): string | null {
  const trimmed = slug?.trim();
  return trimmed ? pageHref(language, trimmed) : null;
}

/** Appends query parameters to an href that may already carry a query string. Empty/nullish
 * values are skipped; returns the href unchanged when nothing is left to append. */
export function withQuery(
  href: string,
  params: Record<string, string | number | null | undefined>,
): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === null || value === undefined || value === "") continue;
    search.append(key, String(value));
  }
  const query = search.toString();
  if (!query) return href;
  return `${href}${href.includes("?") ? "&" : "?"}${query}`;
}

/**
 * Turns an admin-entered URL (slider button, banner card, text-card button) into a
 * language-aware href: absolute URLs, "#" anchors and non-rooted values pass through; rooted
 * app paths get the language prefix unless they already carry it or point at /admin.
 * Returns null for an empty value — the caller decides what "no link" means, this helper never
 * substitutes a guessed destination.
 */
export function localizeHref(url: string | null | undefined, language?: string | null): string | null {
  if (!url) return null;
  if (url.startsWith("http://") || url.startsWith("https://") || url.startsWith("#")) return url;
  if (!url.startsWith("/")) return url;

  const prefix = languagePrefix(language);
  if (!prefix) return url;
  if (url === "/") return prefix;
  if (url.startsWith("/admin")) return url;
  if (url.startsWith(`${prefix}/`)) return url;
  return `${prefix}${url}`;
}

/** Structural shape shared by the public/header/footer menu item types. */
export type MenuItemLike = {
  kind: "page" | "category" | "custom";
  targetSlug?: string;
  url?: string;
};

/** Href for an admin-configured menu entry (Header categories/main menu, Footer columns).
 * Unresolvable entries yield "#", matching the previous per-component copies. */
export function menuItemHref(item: MenuItemLike, language?: string | null): string {
  if (item.kind === "page" && item.targetSlug) return pageHref(language, item.targetSlug);
  if (item.kind === "category" && item.targetSlug) return categoryHref(language, item.targetSlug);
  if (item.kind === "custom" && item.url) return item.url;
  return "#";
}
